import { useContext, useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import {
	ANALYTICS_EVENT,
	ANALYTICS_MONETIZATION_SOURCE,
} from '@/common/constants/analyticsEvents';
import { LanguageContext } from '@/common/contexts/LanguageContext';
import { LANG_OPTIONS } from '@/common/constants/languages';
import { stripePromise } from '@/common/stripe/stripeClient';
import { useGuestAuth } from '@/common/contexts/GuestAuthContext';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { useAnalytics } from '@/common/contexts/AnalyticsContext';
import { formatTranslation } from '@/common/utils/formatTranslation';
import { ALL_PLANS } from '@/pages/customer/pawall/Paywall.constants';
import StripePaymentForm from '@/pages/customer/pawall/subscription/stripe/components/StripePaymentForm';
import { useStripeCheckout } from '@/pages/customer/pawall/subscription/stripe/hooks/useStripeCheckout';
import StripeSubscriptionPayment from '@/pages/customer/pawall/subscription/stripe/v2/StripeSubscriptionPayment';
import BlikOneOffFallbackModal from '@/pages/customer/pawall/subscription/stripe/v2/components/blikOneOffFallback/BlikOneOffFallbackModal';
import StripeBlikOneOffPayment from '@/pages/customer/pawall/one-off/stripe/v2/StripeBlikOneOffPayment';
import { useSecuredPaywallLock } from './hooks/useSecuredPaywallLock';
import {
	HERO_IMAGE,
	PLAN_MARKS,
	PLAN_DURATION_KEYS,
	PLAN_BENEFIT_KEYS,
} from './constants/planCheckout.constants';
import {
	sortPlansPremiumFirst,
	getDefaultPremiumPlanName,
} from './helpers/planCheckout.helpers';
import {
	BackgroundBadge,
	CheckoutFormWrap,
	CheckoutHeader,
	CheckoutPanel,
	CheckoutTitle,
	ModalBackground,
	PanelSubtitle,
	PanelTitle,
	PlanBenefitItem,
	PlanBenefits,
	PlanBody,
	PlanCardsStack,
	PlanIcon,
	PlanMeta,
	PlanModalClose,
	PlanModalOverlay,
	PlanModalShell,
	PlanName,
	PlanPrice,
	PlansContent,
	PlansPanel,
	PaymentStatusBox,
	WowPlanCard,
} from './PlanCheckoutModal.styles';

const PlanCheckoutModal = ({
	secured = false,
	onClose,
	returnPath = '/studio',
	source = ANALYTICS_MONETIZATION_SOURCE.STUDIO,
}) => {
	const { lang } = useContext(LanguageContext);
	const { user } = useGuestAuth();
	const { trackAnalytics } = useAnalytics();
	const t = useLanguageData('chat');
	const plans = sortPlansPremiumFirst(ALL_PLANS[lang] || ALL_PLANS.en);

	const [selectedPlanName, setSelectedPlanName] = useState(() =>
		getDefaultPremiumPlanName(plans),
	);
	const [isBlikFallbackOpen, setIsBlikFallbackOpen] = useState(false);
	const [isBlikOneOff, setIsBlikOneOff] = useState(false);
	const isCheckoutV2 = lang === LANG_OPTIONS.polish;
	const selectedPlan = plans.find((plan) => plan.name === selectedPlanName);
	const { options: elementsOptions } = useStripeCheckout({
		selectedPlan,
		lang,
	});

	useSecuredPaywallLock(secured);

	useEffect(() => {
		trackAnalytics(ANALYTICS_EVENT.PLAN.MODAL_OPEN, {
			source,
			returnPath,
		});
	}, []);

	const handlePlanSelect = (planName) => {
		trackAnalytics(ANALYTICS_EVENT.PLAN.SELECT, {
			planName,
			source,
		});
		setSelectedPlanName(planName);
		setIsBlikFallbackOpen(false);
		setIsBlikOneOff(false);
	};

	const handleBlikFallbackAccept = () => {
		setIsBlikFallbackOpen(false);
		setIsBlikOneOff(true);
	};

	return (
		<PlanModalOverlay
			$secured={secured}
			role='dialog'
			aria-modal='true'
			aria-labelledby='planCheckoutTitle'
			aria-hidden='false'
			onClick={secured ? undefined : onClose}
		>
			<PlanModalShell onClick={(event) => event.stopPropagation()}>
				{!secured && onClose && (
					<PlanModalClose
						type='button'
						aria-label={t.planCheckoutClose}
						onClick={onClose}
					>
						×
					</PlanModalClose>
				)}

				<ModalBackground aria-hidden='true'>
					<img src={HERO_IMAGE} alt='' />
				</ModalBackground>
				<BackgroundBadge>{t.planPaywallHeroTitle}</BackgroundBadge>

				<PlansPanel>
					<PlansContent>
						<PanelTitle id='planCheckoutTitle'>{t.planPaywallTitle}</PanelTitle>
						<PanelSubtitle>{t.planPaywallDescription}</PanelSubtitle>

						<PlanBenefits>
							{PLAN_BENEFIT_KEYS.map((key) => (
								<PlanBenefitItem key={key}>{t[key]}</PlanBenefitItem>
							))}
						</PlanBenefits>

						<PlanCardsStack
							role='radiogroup'
							aria-label={t.planPaywallSelectPlan}
						>
							{plans.map((plan) => {
								const isSelected = selectedPlanName === plan.name;
								const durationKey = PLAN_DURATION_KEYS[plan.name];
								const durationLabel = durationKey
									? t[durationKey]
									: plan.features;

								return (
									<WowPlanCard
										key={plan.name}
										type='button'
										role='radio'
										aria-checked={isSelected}
										$selected={isSelected}
										$popular={plan.isPopular}
										$popularLabel={t.planPaywallPopular}
										onClick={() => handlePlanSelect(plan.name)}
									>
										<PlanIcon $selected={isSelected}>
											{PLAN_MARKS[plan.name] || plan.name[0]}
										</PlanIcon>
										<PlanBody>
											<PlanName>{plan.name}</PlanName>
											<PlanMeta>{durationLabel}</PlanMeta>
										</PlanBody>
										<PlanPrice>{plan.price}</PlanPrice>
									</WowPlanCard>
								);
							})}
						</PlanCardsStack>
					</PlansContent>
				</PlansPanel>

				<CheckoutPanel aria-labelledby='planCheckoutPaymentTitle'>
					<CheckoutHeader>
						<CheckoutTitle id='planCheckoutPaymentTitle'>
							{t.planCheckoutPaymentLabel}
						</CheckoutTitle>
					</CheckoutHeader>

					<CheckoutFormWrap>
						{!stripePromise && (
							<PaymentStatusBox $error>
								{t.planCheckoutStripeMissing}
							</PaymentStatusBox>
						)}

						{!user?.id && (
							<PaymentStatusBox>{t.planCheckoutLoginRequired}</PaymentStatusBox>
						)}

						{isBlikFallbackOpen && (
							<BlikOneOffFallbackModal
								description={formatTranslation(
									t.planCheckoutBlikFallbackDescription,
									{
										price: selectedPlan?.price,
										duration: t[PLAN_DURATION_KEYS[selectedPlan?.name]],
									},
								)}
								onAccept={handleBlikFallbackAccept}
								onDecline={() => setIsBlikFallbackOpen(false)}
							/>
						)}

						{!isCheckoutV2 &&
							user?.id &&
							stripePromise &&
							elementsOptions &&
							selectedPlan && (
								<Elements
									key={selectedPlan.name}
									stripe={stripePromise}
									options={elementsOptions}
								>
									<StripePaymentForm
										selectedPlan={selectedPlan}
										returnPath={returnPath}
										analyticsSource={source}
									/>
								</Elements>
							)}

						{isCheckoutV2 &&
							user?.id &&
							stripePromise &&
							selectedPlan &&
							isBlikOneOff && (
								<StripeBlikOneOffPayment
									selectedPlan={selectedPlan}
									returnPath={returnPath}
									analyticsSource={source}
									onBack={() => setIsBlikOneOff(false)}
								/>
							)}

						{isCheckoutV2 &&
							user?.id &&
							stripePromise &&
							selectedPlan &&
							!isBlikOneOff && (
								<StripeSubscriptionPayment
									key={selectedPlan.name}
									selectedPlan={selectedPlan}
									returnPath={returnPath}
									analyticsSource={source}
									onBlikRejected={() => setIsBlikFallbackOpen(true)}
								/>
							)}
					</CheckoutFormWrap>
				</CheckoutPanel>
			</PlanModalShell>
		</PlanModalOverlay>
	);
};

export default PlanCheckoutModal;
